import {useMemo} from "react";

import {buildComboChart, buildLineChart, SEMANTIC_COLORS} from "@components/charts/echarts-toolkit";
import {EChartsCard} from "@components/charts/EChartsCard";

import type {XeroPnLMonthlyPerf} from "@hooks/use-vnext";

interface XeroPnlTrendChartsProps {
    months: XeroPnLMonthlyPerf[];
}

function monthLabel(monthKey: string): string {
    const [year, month] = monthKey.split("-").map(Number);
    if (!year || !month) return monthKey;
    return new Date(year, month - 1, 1).toLocaleString("en-US", {month: "short"});
}

export function XeroPnlTrendCharts({months}: XeroPnlTrendChartsProps) {
    const chartData = useMemo(
        () =>
            months.map((m) => ({
                month: monthLabel(m.month_key),
                monthKey: m.month_key,
                status: m.status,
                revenue: m.revenue,
                grossProfit: m.gross_profit,
                netProfit: m.net_profit,
                grossMargin: m.gross_margin_pct,
                netMargin: m.net_margin_pct,
            })),
        [months],
    );

    const firstForecastMonth = useMemo(() => {
        const idx = chartData.findIndex((m) => m.status !== "actual");
        return idx >= 0 ? chartData[idx].month : undefined;
    }, [chartData]);

    const revenueOption = useMemo(
        () =>
            buildComboChart({
                categories: chartData.map((m) => m.month),
                barSeries: [
                    {
                        key: "revenue",
                        label: "Revenue",
                        data: chartData.map((m) => m.revenue),
                        color: SEMANTIC_COLORS.primary,
                    },
                ],
                lineSeries: [
                    {
                        key: "netProfit",
                        label: "Net Profit",
                        data: chartData.map((m) => m.netProfit),
                        color: SEMANTIC_COLORS.accent,
                    },
                ],
                yFormat: "compact_currency",
                forecastStart: firstForecastMonth,
            }),
        [chartData, firstForecastMonth],
    );

    const marginOption = useMemo(
        () =>
            buildLineChart({
                categories: chartData.map((m) => m.month),
                series: [
                    {
                        key: "grossMargin",
                        label: "Gross Margin",
                        data: chartData.map((m) => m.grossMargin),
                        color: SEMANTIC_COLORS.primary,
                    },
                    {
                        key: "netMargin",
                        label: "Net Margin",
                        data: chartData.map((m) => m.netMargin),
                        color: SEMANTIC_COLORS.accent,
                    },
                ],
                yFormat: "percent",
                forecastStart: firstForecastMonth,
                smooth: true,
            }),
        [chartData, firstForecastMonth],
    );

    if (!chartData.length) return null;

    return (
        <div className="grid gap-6 xl:grid-cols-2">
            <section className="rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
                <h3 className="mb-3 text-sm font-bold uppercase tracking-[2px] text-[#209DB3]">
                    Revenue and Net Profit Trend
                </h3>
                <EChartsCard option={revenueOption} height={300} />
            </section>

            <section className="rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
                <h3 className="mb-3 text-sm font-bold uppercase tracking-[2px] text-[#209DB3]">
                    Margin Trend
                </h3>
                <EChartsCard option={marginOption} height={300} />
            </section>
        </div>
    );
}
